import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  Button,
  FieldError,
  Form,
  Label,
  ListBox,
  Modal,
  Select,
  Switch,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import { useUpdateDirection } from "@/features/directions/use-directions.ts";
import { DirectionEntity } from "@/features/directions/type.ts";
import { useCities } from "@/features/city/use-cities.ts";

interface IFormInput {
  fromCityId: string;
  toCityId: string;
  isActive: boolean;
}

export function EditDirectionModal({
  direction,
}: {
  direction: DirectionEntity;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { mutateAsync, isPending } = useUpdateDirection({
    onSuccess: () => {
      reset();
      setIsOpen(false);
    },
  });
  const { data: citiesData } = useCities();
  const cities = citiesData.data.map((item) => ({
    key: `${item.id}`,
    title: item.name,
  }));

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IFormInput>();

  const onSubmit: SubmitHandler<IFormInput> = (data) =>
    mutateAsync({
      id: direction.id,
      fromCityId: Number(data.fromCityId),
      toCityId: Number(data.toCityId),
      isActive: data.isActive,
    });

  return (
    <>
      <Button isIconOnly variant="ghost" onPress={() => setIsOpen(true)}>
        <Icon className="size-6" icon="solar:pen-bold-duotone" />
      </Button>

      <Modal isOpen={isOpen} onOpenChange={setIsOpen}>
        <Modal.Backdrop isDismissable>
          <Modal.Container placement="center" size="lg">
            <Modal.Dialog>
              <Form
                className="flex flex-col pt-4 pr-4"
                onSubmit={handleSubmit(onSubmit)}
              >
                <Modal.Header>
                  <Modal.Heading>Изменить направление</Modal.Heading>
                </Modal.Header>

                <Modal.Body className="grid grid-cols-2 gap-4">
                  <Controller
                    control={control}
                    defaultValue={direction.fromCity.id.toString()}
                    name="fromCityId"
                    render={({ field }) => (
                      <Select
                        isInvalid={!!errors.fromCityId?.message}
                        selectedKey={field.value ?? null}
                        onSelectionChange={(key) => field.onChange(key)}
                      >
                        <Label>Откуда</Label>
                        <Select.Trigger>
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <FieldError>{errors.fromCityId?.message}</FieldError>
                        <Select.Popover>
                          <ListBox items={cities}>
                            {({ key, title }) => (
                              <ListBox.Item id={key} textValue={title}>
                                {title}
                              </ListBox.Item>
                            )}
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    )}
                    rules={{ required: "Нужно заполнить поле" }}
                  />

                  <Controller
                    control={control}
                    defaultValue={direction.toCity.id.toString()}
                    name="toCityId"
                    render={({ field }) => (
                      <Select
                        isInvalid={!!errors.toCityId?.message}
                        selectedKey={field.value ?? null}
                        onSelectionChange={(key) => field.onChange(key)}
                      >
                        <Label>Куда</Label>
                        <Select.Trigger>
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <FieldError>{errors.toCityId?.message}</FieldError>
                        <Select.Popover>
                          <ListBox items={cities}>
                            {({ key, title }) => (
                              <ListBox.Item id={key} textValue={title}>
                                {title}
                              </ListBox.Item>
                            )}
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    )}
                    rules={{ required: "Нужно заполнить поле" }}
                  />

                  <Controller
                    control={control}
                    defaultValue={direction.isActive}
                    name="isActive"
                    render={({ field }) => (
                      <Switch
                        isSelected={field.value}
                        onChange={field.onChange}
                      >
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                        <Switch.Content>
                          <Label>Видимое</Label>
                        </Switch.Content>
                      </Switch>
                    )}
                  />
                </Modal.Body>

                <Modal.Footer>
                  <Button
                    variant="danger-soft"
                    onPress={() => setIsOpen(false)}
                  >
                    Отмена
                  </Button>

                  <Button isPending={isPending} type="submit" variant="primary">
                    Сохранить
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </>
  );
}
